import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../services/api";

/* ─── helpers ─── */
const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

const GENDER_COLORS = { Male:"bg-blue-100 text-blue-700", Female:"bg-pink-100 text-pink-700", Other:"bg-purple-100 text-purple-700" };

/* ═══════════════════════════════════════════════════════════════
   STAT CARD
═══════════════════════════════════════════════════════════════ */
function StatCard({ icon, label, value, color }) {
  return (
    <motion.div whileHover={{ y: -3 }} className="bg-white rounded-2xl shadow-sm p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${color}`}>{icon}</div>
      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value ?? "—"}</p>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PATIENT DETAIL DRAWER
═══════════════════════════════════════════════════════════════ */
function PatientDrawer({ patientId, onClose }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    api.get(`/mrd/patients/${patientId}`)
      .then(r => setRec(r.data))
      .catch(() => setRec(null))
      .finally(() => setLoading(false));
  }, [patientId]);

  return (
    <motion.div
      initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
      transition={{ type: "spring", stiffness: 300, damping: 32 }}
      className="fixed top-0 right-0 h-full w-full md:w-[520px] bg-white shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Patient Record</h2>
          <p className="text-xs text-gray-400">Medical Records Department</p>
        </div>
        <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 text-xl transition">✕</button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading && (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Loading record…</p>
          </div>
        )}

        {!loading && !rec && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-2">🗂️</p>
            <p>Record not found.</p>
          </div>
        )}

        {!loading && rec && (
          <div className="space-y-6">
            {/* Demographics */}
            <section className="bg-gray-50 rounded-2xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-800 text-xl">{rec.patient.name}</h3>
                  <p className="text-sm text-green-700 font-mono font-semibold">{rec.patient.patientId}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${GENDER_COLORS[rec.patient.gender] || "bg-gray-100 text-gray-600"}`}>
                  {rec.patient.gender || "—"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoRow icon="🎂" label="Age" val={rec.patient.age ? `${rec.patient.age} yrs` : "—"} />
                <InfoRow icon="📞" label="Phone" val={rec.patient.phone || "—"} />
                <InfoRow icon="📍" label="Address" val={rec.patient.address || "—"} />
                <InfoRow icon="📅" label="Registered" val={fmt(rec.patient.createdAt)} />
                {rec.patient.createdByDoctor && (
                  <InfoRow icon="👨‍⚕️" label="Doctor" val={`Dr. ${rec.patient.createdByDoctor.name}`} />
                )}
                <InfoRow icon="🏥" label="Type" val={rec.patient.isWalkIn ? "Walk-in" : "Registered"} />
              </div>
            </section>

            {/* Prescriptions */}
            <section>
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>📋</span> Prescription History
                <span className="ml-auto bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  {rec.prescriptions.length} records
                </span>
              </h4>

              {rec.prescriptions.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No prescriptions recorded yet.</p>
              )}

              <div className="space-y-3">
                {rec.prescriptions.map((p, i) => (
                  <div key={p._id} className="border border-gray-100 rounded-xl p-4 hover:border-green-200 transition">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-bold text-gray-500">#{rec.prescriptions.length - i}</span>
                      <span className="text-xs text-gray-400">{fmtTime(p.createdAt)}</span>
                    </div>
                    {p.doctor && (
                      <p className="text-xs text-green-700 mb-2">Dr. {p.doctor.name} · {p.doctor.qualification}</p>
                    )}
                    {p.diagnosis && (
                      <p className="text-sm font-medium text-gray-700 mb-1">🔬 {p.diagnosis}</p>
                    )}
                    {p.medicines && p.medicines.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {p.medicines.slice(0, 4).map((m, mi) => (
                          <p key={mi} className="text-xs text-gray-500">
                            • <span className="font-medium">{m.name}</span>
                            {m.dosage && ` — ${m.dosage}`}
                            {m.duration && ` for ${m.duration}`}
                          </p>
                        ))}
                        {p.medicines.length > 4 && (
                          <p className="text-xs text-gray-400">+{p.medicines.length - 4} more…</p>
                        )}
                      </div>
                    )}
                    {p.notes && (
                      <p className="text-xs text-gray-400 mt-2 italic">Notes: {p.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function InfoRow({ icon, label, val }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-base">{icon}</span>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm font-medium text-gray-700">{val}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MRD PERMISSION MANAGER (Master Admin only)
═══════════════════════════════════════════════════════════════ */
function MRDPermissionManager() {
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setAdmins(r.data.filter(a => a.role !== "MASTER_ADMIN"));
    } catch {
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (admin) => {
    const hasMRD = admin.permissions?.includes("mrd");
    setSaving(admin._id);
    try {
      await api.put(`/mrd/grant/${admin._id}`, { grant: !hasMRD });
      setAdmins(prev => prev.map(a =>
        a._id === admin._id
          ? { ...a, permissions: hasMRD ? a.permissions.filter(p => p !== "mrd") : [...(a.permissions || []), "mrd"] }
          : a
      ));
    } catch {/* ignore */}
    finally { setSaving(null); }
  };

  if (loading) return <div className="py-6 text-center text-gray-400 text-sm">Loading admins…</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
        <span className="text-xl">🔐</span>
        <div>
          <h3 className="font-semibold text-gray-800">MRD Access Control</h3>
          <p className="text-xs text-gray-400">Grant or revoke Medical Records access per admin</p>
        </div>
      </div>
      <div className="divide-y divide-gray-50">
        {admins.length === 0 && (
          <p className="text-center py-8 text-sm text-gray-400">No sub-admins found. Create admins in Manage Users.</p>
        )}
        {admins.map(a => {
          const hasMRD = a.permissions?.includes("mrd");
          return (
            <div key={a._id} className="flex items-center justify-between px-6 py-3.5 hover:bg-gray-50 transition">
              <div>
                <p className="font-medium text-sm text-gray-800">{a.name}</p>
                <p className="text-xs text-gray-400">{a.email} · <span className="text-gray-500">{a.role}</span>{a.section ? ` · ${a.section}` : ""}</p>
              </div>
              <button
                onClick={() => toggle(a)}
                disabled={saving === a._id}
                className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none ${hasMRD ? "bg-green-500" : "bg-gray-200"} ${saving === a._id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span className={`inline-block w-4 h-4 transform bg-white rounded-full shadow transition-transform duration-200 ${hasMRD ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════ */
export default function AdminMRD() {
  const admin = (() => {
    try { return JSON.parse(sessionStorage.getItem("adminUser") || "null"); } catch { return null; }
  })();

  const isMaster = admin?.role === "MASTER_ADMIN";
  const hasMRD   = isMaster || admin?.permissions?.includes("mrd");

  /* stats */
  const [stats,   setStats]   = useState(null);
  /* patient list */
  const [patients, setPatients] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(false);
  /* filters */
  const [search,   setSearch]   = useState("");
  const [gender,   setGender]   = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [from,     setFrom]     = useState("");
  const [to,       setTo]       = useState("");
  const [doctors,  setDoctors]  = useState([]);
  /* detail drawer */
  const [activeId, setActiveId] = useState(null);
  /* active tab */
  const [tab, setTab] = useState("patients"); // "patients" | "access"

  const LIMIT = 30;

  const loadStats = useCallback(async () => {
    try { const r = await api.get("/mrd/stats"); setStats(r.data); } catch {/* */}
  }, []);

  const loadDoctors = useCallback(async () => {
    try { const r = await api.get("/mrd/doctors"); setDoctors(r.data); } catch {/* */}
  }, []);

  const loadPatients = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: LIMIT };
      if (search.trim())  params.q        = search.trim();
      if (gender)         params.gender   = gender;
      if (doctorId)       params.doctorId = doctorId;
      if (from)           params.from     = from;
      if (to)             params.to       = to;
      const r = await api.get("/mrd/patients", { params });
      setPatients(r.data.patients || []);
      setTotal(r.data.total || 0);
      setPage(p);
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [search, gender, doctorId, from, to]);

  useEffect(() => {
    if (!hasMRD) return;
    loadStats();
    loadDoctors();
  }, [hasMRD, loadStats, loadDoctors]);

  useEffect(() => {
    if (!hasMRD) return;
    const t = setTimeout(() => loadPatients(1), 300);
    return () => clearTimeout(t);
  }, [hasMRD, loadPatients]);

  /* no access */
  if (!hasMRD) {
    return (
      <div className="flex flex-col items-center justify-center h-72 gap-3 text-center">
        <span className="text-5xl">🔒</span>
        <h2 className="text-xl font-bold text-gray-700">MRD Access Restricted</h2>
        <p className="text-sm text-gray-400 max-w-xs">You do not have permission to view Medical Records.<br />Please contact the Master Admin.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-6">
      {/* PAGE HEADER */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Medical Records Department</h1>
          <p className="text-sm text-gray-400">View patient registry and medical history</p>
        </div>
        <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full">🏥 MRD</span>
      </div>

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon="👥" label="Total Patients"  value={stats.total}      color="bg-blue-50"   />
          <StatCard icon="🗓️" label="Today's OPD"    value={stats.todayCount} color="bg-yellow-50" />
          <StatCard icon="📈" label="This Month"      value={stats.thisMonth}  color="bg-purple-50" />
          <StatCard icon="🩺" label="Active Doctors"  value={stats.doctorCount} color="bg-green-50" />
        </div>
      )}

      {/* TABS (Master Admin sees access control tab) */}
      {isMaster && (
        <div className="flex gap-2">
          {[["patients","🗂️ Patient Records"], ["access","🔐 Access Control"]].map(([k, label]) => (
            <button
              key={k} onClick={() => setTab(k)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition ${tab === k ? "bg-green-600 text-white shadow-sm" : "bg-white text-gray-600 hover:bg-gray-100"}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ACCESS CONTROL TAB */}
      {tab === "access" && isMaster && <MRDPermissionManager />}

      {/* PATIENTS TAB */}
      {tab === "patients" && (
        <>
          {/* FILTERS */}
          <div className="bg-white rounded-2xl shadow-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <input
              className="col-span-1 sm:col-span-2 lg:col-span-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="🔍 Search name / phone / patient ID"
              value={search} onChange={e => setSearch(e.target.value)}
            />
            <select
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              value={gender} onChange={e => setGender(e.target.value)}
            >
              <option value="">All Genders</option>
              <option>Male</option><option>Female</option><option>Other</option>
            </select>
            <select
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              value={doctorId} onChange={e => setDoctorId(e.target.value)}
            >
              <option value="">All Doctors</option>
              {doctors.map(d => <option key={d._id} value={d._id}>Dr. {d.name}</option>)}
            </select>
            <div className="flex gap-2 col-span-1">
              <input type="date" className="flex-1 border border-gray-200 rounded-xl px-2 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-400" value={from} onChange={e => setFrom(e.target.value)} title="From date" />
              <input type="date" className="flex-1 border border-gray-200 rounded-xl px-2 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-400" value={to} onChange={e => setTo(e.target.value)} title="To date" />
            </div>
          </div>

          {/* RESULT COUNT */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              {loading ? "Loading…" : `${total.toLocaleString()} patient${total !== 1 ? "s" : ""} found`}
            </span>
            {(search || gender || doctorId || from || to) && (
              <button
                className="text-xs text-red-500 hover:underline"
                onClick={() => { setSearch(""); setGender(""); setDoctorId(""); setFrom(""); setTo(""); }}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* TABLE */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["Patient ID", "Name", "Age / Gender", "Phone", "Doctor", "Registered", ""].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading && (
                    <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                        Loading patients…
                      </div>
                    </td></tr>
                  )}
                  {!loading && patients.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-16 text-gray-400">
                      <p className="text-4xl mb-2">🗂️</p>
                      <p>No patients found.</p>
                    </td></tr>
                  )}
                  {!loading && patients.map(p => (
                    <motion.tr
                      key={p._id} whileHover={{ backgroundColor: "#f0fdf4" }}
                      className="cursor-pointer transition-colors" onClick={() => setActiveId(p._id)}
                    >
                      <td className="px-5 py-3.5 font-mono text-xs font-bold text-green-700 whitespace-nowrap">{p.patientId}</td>
                      <td className="px-5 py-3.5 font-medium text-gray-800">{p.name}</td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${GENDER_COLORS[p.gender] || "bg-gray-100 text-gray-500"}`}>
                          {p.age ? `${p.age}y` : "—"} {p.gender || ""}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap">{p.phone || "—"}</td>
                      <td className="px-5 py-3.5 text-gray-500 whitespace-nowrap">
                        {p.createdByDoctor ? `Dr. ${p.createdByDoctor.name}` : "—"}
                      </td>
                      <td className="px-5 py-3.5 text-gray-400 text-xs whitespace-nowrap">{fmt(p.createdAt)}</td>
                      <td className="px-5 py-3.5">
                        <span className="text-green-600 text-xs font-medium hover:underline">View →</span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50">
                <span className="text-xs text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => loadPatients(page - 1)}
                    className="px-4 py-1.5 text-xs rounded-lg bg-white border border-gray-200 disabled:opacity-40 hover:bg-gray-100 transition"
                  >← Prev</button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => loadPatients(page + 1)}
                    className="px-4 py-1.5 text-xs rounded-lg bg-white border border-gray-200 disabled:opacity-40 hover:bg-gray-100 transition"
                  >Next →</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* DETAIL DRAWER */}
      <AnimatePresence>
        {activeId && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setActiveId(null)}
            />
            <PatientDrawer patientId={activeId} onClose={() => setActiveId(null)} />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
